import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import StripeTrustBanner from '../components/StripeTrustBanner';
import { LanguageProvider } from '../context/LanguageContext';
import {
 ShoppingBag,
 QrCode,
 CreditCard,
 Bell,
 Printer,
 Receipt,
 LayoutDashboard,
 Truck,
 Check,
 ArrowRight,
 Sparkles,
 Shield,
} from 'lucide-react';

const features = [
 {
 icon: ShoppingBag,
 title: 'Online ordering',
 desc: 'A premium digital menu your customers can order from in seconds — pickup or delivery.',
 },
 {
 icon: QrCode,
 title: 'QR ordering',
 desc: 'Generate QR codes for tables and counters. Customers scan, order, and pay instantly.',
 },
 {
 icon: CreditCard,
 title: 'Apple Pay · Google Pay',
 desc: 'Frictionless checkout with the payment methods US customers expect.',
 },
 {
 icon: Shield,
 title: 'Direct payouts',
 desc: 'Payments go directly to your own Stripe account. NoFee never holds your money.',
 },
 {
 icon: Bell,
 title: 'Customer notifications',
 desc: 'Automatic SMS & email updates from order received to "ready for pickup".',
 },
 {
 icon: Truck,
 title: 'Delivery & takeout',
 desc: 'Manage both flows from a single, modern dashboard.',
 },
 {
 icon: Printer,
 title: 'Kitchen ticket printing',
 desc: 'One-tap kitchen ticket for every order — paper or thermal printer ready.',
 },
 {
 icon: Receipt,
 title: 'Printable receipts',
 desc: 'Branded customer receipts your team can print or share digitally.',
 },
 {
 icon: LayoutDashboard,
 title: 'Modern dashboard',
 desc: 'Live orders, payouts, customers, and analytics — all in one premium interface.',
 },
];

const includes = [
 '0% commission on every order — forever',
 '45-day free trial — cancel anytime',
 'Direct Stripe payouts to your own account',
 'Premium digital menu + QR ordering',
 'Apple Pay · Google Pay checkout',
 'SMS / Email customer notifications',
 'Kitchen tickets & printable receipts',
 'Customer rewards & loyalty program',
];

const NoFeeOrdering = () => {
 return (
 <LanguageProvider>
 <SEO
 title="NoFee Ordering — Restaurant Online Ordering System ($299/mo, 0% Commission)"
 description="The NoFee Ordering platform: custom digital menu, QR-code & WhatsApp ordering, direct Stripe payouts. $299/month flat — 0% commission on every order."
 path="/for-restaurants/ordering"
 />
 <div className="min-h-screen bg-black text-white">
 <Navbar />

 {/* Hero */}
 <section className="relative pt-32 sm:pt-36 lg:pt-44 pb-16 lg:pb-20">
 <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.10),transparent_60%)]" />
 <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
 <Link
 to="/for-restaurants"
 className="inline-flex items-center gap-1.5 text-xs text-white/55 hover:text-gold-300 transition-colors mb-6"
 data-testid="back-to-solutions"
 >
 ← Back to Restaurant Solutions
 </Link>

 <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gold-500/30 bg-gold-500/5 mb-6">
 <ShoppingBag className="w-3.5 h-3.5 text-gold-300" />
 <span className="text-[11px] uppercase tracking-[0.22em] text-gold-300 font-semibold">
 NoFee Ordering
 </span>
 </div>

 <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] max-w-3xl">
 0% commission
 <br />
 <span className="bg-gradient-to-r from-amber-200 via-gold-400 to-amber-300 bg-clip-text text-transparent">
 online ordering.
 </span>
 </h1>
 <p className="mt-6 max-w-2xl text-base sm:text-lg text-white/65 leading-relaxed">
 A premium ordering experience for your restaurant — online, on QR codes,
 and on every device. Direct payouts. No commissions. Forever.
 </p>

 <div className="mt-8 flex flex-col sm:flex-row gap-3">
 <Link
 to="/register?next=billing"
 data-testid="ordering-start-trial"
 className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-bold transition-all hover:shadow-[0_15px_40px_-15px_rgba(212,175,55,0.6)]"
 >
 Start 45-Day Free Trial
 <ArrowRight className="w-4 h-4" />
 </Link>
 <Link
 to="/for-restaurants/pos-lite"
 className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl border border-white/15 hover:border-gold-500/40 text-white/85 hover:text-white font-medium transition-all"
 data-testid="ordering-see-pos"
 >
 See POS Lite
 </Link>
 </div>

 <p className="mt-4 text-xs text-white/45">
 $299/month after trial · No contracts · Cancel anytime
 </p>
 </div>
 </section>

 {/* Features grid */}
 <section className="relative pb-20 lg:pb-28 border-t border-white/[0.05]">
 <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-20">
 <div className="max-w-2xl mb-12">
 <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-semibold mb-3">
 Everything included
 </p>
 <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
 Built for modern restaurants.
 </h2>
 <p className="mt-3 text-white/60">
 Every feature you need to run a premium ordering operation — nothing you don't.
 </p>
 </div>

 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
 {features.map((f) => (
 <div
 key={f.title}
 className="group rounded-2xl border border-white/10 bg-zinc-950/60 p-6 hover:border-gold-500/30 hover:bg-zinc-950 transition-all duration-300"
 data-testid={`ordering-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
 >
 <div className="w-10 h-10 rounded-xl bg-gold-500/10 border border-gold-500/20 flex items-center justify-center mb-4 group-hover:bg-gold-500/15 transition-colors">
 <f.icon className="w-4.5 h-4.5 text-gold-300" />
 </div>
 <h3 className="text-base font-bold text-white">{f.title}</h3>
 <p className="mt-1.5 text-[13.5px] text-white/55 leading-relaxed">
 {f.desc}
 </p>
 </div>
 ))}
 </div>
 </div>
 </section>

 {/* Pricing card */}
 <section className="relative pb-24 lg:pb-32 border-t border-white/[0.05]">
 <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-20">
 <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
 <div>
 <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-semibold mb-3">
 Simple, transparent pricing
 </p>
 <h2 className="text-3xl sm:text-4xl font-black tracking-tight">
 One flat price. Zero commission.
 </h2>
 <p className="mt-4 text-white/65 max-w-md">
 Keep more from every order. NoFee Ordering pays for
 itself with just a handful of orders per week.
 </p>

 <ul className="mt-8 space-y-3">
 {includes.map((i) => (
 <li key={i} className="flex items-start gap-3 text-[14.5px] text-white/80">
 <Check className="w-4 h-4 text-gold-400 flex-shrink-0 mt-1" />
 <span>{i}</span>
 </li>
 ))}
 </ul>
 </div>

 <div className="rounded-3xl border border-gold-500/30 bg-gradient-to-br from-zinc-950 via-black to-zinc-950 p-8 sm:p-10 shadow-[0_20px_80px_-30px_rgba(212,175,55,0.45)]">
 <div className="flex items-center gap-2 mb-5">
 <Sparkles className="w-4 h-4 text-gold-300" />
 <span className="text-[11px] uppercase tracking-[0.22em] text-gold-300 font-semibold">
 NoFee Ordering
 </span>
 </div>
 <div className="flex items-baseline gap-2">
 <span className="text-5xl font-black text-white">$299</span>
 <span className="text-white/55">/ month</span>
 </div>
 <p className="text-xs text-white/45 mt-2">
 Billed monthly · 45-day free trial included
 </p>

 <Link
 to="/start?plan=ordering"
 data-testid="ordering-pricing-cta"
 className="mt-8 inline-flex w-full items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 text-black font-bold transition-all hover:shadow-[0_15px_40px_-15px_rgba(212,175,55,0.6)]"
 >
 Start Free Trial
 <ArrowRight className="w-4 h-4" />
 </Link>
 <p className="text-center text-[11px] text-white/40 mt-3">
 No credit card required to start
 </p>

 <div className="mt-6">
 <StripeTrustBanner variant="card" dark />
 </div>
 </div>
 </div>
 </div>
 </section>

 <FooterSection />
 </div>
 </LanguageProvider>
 );
};

export default NoFeeOrdering;
